"use client";

import { useHistory } from "react-router-dom";

import { Button } from "@/components/ui/button-tw";
import { Checkbox } from "@/components/ui/check-box";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, Filter, Download } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Calendar } from "@/components/ui/calendar";
import {
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/providers/auth";
import { exportToCsv } from "@/scripts/functions";

import TablePagination from "@/components/table-pagination";
import MainTable from "@/components/main-table";
import TableColumnsManager from "@/components/table-columns-manager";
import SearchInput from "@/components/search-input";
import { useTransactions } from "@/hooks/transaction/use-transactions";
import { useTransactionDrawer } from "@/hooks/transaction/use-transaction-drawer";
import { TransactionStatusBadge } from "./transaction-status-badge";

// Custom filter function for multi-column searching
const multiColumnFilterFn = (row, _columnId, filterValue) => {
  const searchableRowContent = `${
    row.original.transaction_from_description || ""
  } ${row.original.transaction_to_description || ""}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

const statusFilterFn = (row, columnId, filterValue) => {
  if (!filterValue?.length) return true;
  const status = row.getValue(columnId);
  return filterValue.includes(status);
};

const amountFilterFn = (row, columnId, filterValue) => {
  if (!filterValue?.length) return true;
  const amount =
    row.original.transaction_direction === "debit"
      ? row.getValue(columnId)
      : -row.getValue(columnId);
  return filterValue[0] <= amount && filterValue[1] >= amount;
};

const dateCreatedFilterFn = (row, columnId, filterValue) => {
  if (!filterValue) return true;

  const createdDate = dayjs(row.getValue(columnId));
  const fromDate = dayjs(filterValue.from).subtract(1, "day");
  const toDate = dayjs(filterValue.to).add(1, "day");
  return (
    createdDate.isAfter(fromDate, "day") && createdDate.isBefore(toDate, "day")
  );
};

const columns = [
  {
    id: "date",
    header: "Date",
    accessorFn: (row) => dayjs(row.transaction_date).format("MM/DD/YYYY"),
    size: 170,
    filterFn: dateCreatedFilterFn,
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.original.transaction_date;
      const b = rowB.original.transaction_date;

      if (!a) return -1;
      if (!b) return 1;

      const diff = dayjs(a).diff(b);

      if (diff < 0) return -1;
      if (diff > 0) return 1;
      return 0;
    },
  },
  {
    id: "amount",
    header: "Amount",
    accessorKey: "amount",
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("amount"));
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);

      return row.original.transaction_direction === "debit"
        ? formatted
        : `-${formatted}`;
    },
    size: 120,
    // filterFn: amountFilterFn,
  },
  {
    id: "from",
    header: "From",
    accessorKey: "transaction_from_description",
    size: 220,
    filterFn: multiColumnFilterFn,
  },
  {
    id: "to",
    header: "To",
    accessorKey: "transaction_to_description",
    size: 220,
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "transaction_status",
    cell: ({ row }) => (
      <TransactionStatusBadge status={row.getValue("status")} />
    ),
    size: 170,
    enableSorting: false,
    filterFn: statusFilterFn,
  },
];

const fallbackData = [];

export default function TransactionTable() {
  const { data, isLoading } = useTransactions();
  const { handleOpen: openTransactionDrawer } = useTransactionDrawer();
  const history = useHistory();
  const id = useId();
  const tableRef = useRef(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState([]);
  const table = useReactTable({
    data: data || fallbackData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  // Get unique status values
  const uniqueStatusValues = useMemo(() => {
    const statusColumn = table.getColumn("status");

    if (!statusColumn) return [];

    const values = Array.from(statusColumn.getFacetedUniqueValues().keys());

    return values.sort();
  }, [table.getColumn("status")?.getFacetedUniqueValues()]);

  // Get counts for each status
  const statusCounts = useMemo(() => {
    const statusColumn = table.getColumn("status");
    if (!statusColumn) return new Map();
    return statusColumn.getFacetedUniqueValues();
  }, [table.getColumn("status")?.getFacetedUniqueValues()]);

  const selectedStatuses = useMemo(() => {
    const filterValue = table.getColumn("status")?.getFilterValue();
    return filterValue ?? [];
  }, [table.getColumn("status")?.getFilterValue()]);

  const handleStatusChange = (checked, value) => {
    const filterValue = table.getColumn("status")?.getFilterValue();
    const newFilterValue = filterValue ? [...filterValue] : [];

    if (checked) {
      newFilterValue.push(value);
    } else {
      const index = newFilterValue.indexOf(value);
      if (index > -1) {
        newFilterValue.splice(index, 1);
      }
    }

    table
      .getColumn("status")
      ?.setFilterValue(newFilterValue.length ? newFilterValue : undefined);
  };

  // const minAmount = useMemo(() => {
  //   return data?.reduce(
  //     (acc, transaction) =>
  //       acc <
  //       (transaction.transaction_direction === "debit"
  //         ? transaction.amount
  //         : -transaction.amount)
  //         ? acc
  //         : transaction.amount,
  //     Infinity
  //   );
  // }, [data]);

  // const maxAmount = useMemo(() => {
  //   return data?.reduce(
  //     (acc, transaction) =>
  //       acc >
  //       (transaction.transaction_direction === "debit"
  //         ? transaction.amount
  //         : -transaction.amount)
  //         ? acc
  //         : transaction.amount,
  //     -Infinity
  //   );
  // }, [data]);

  // useEffect(() => {
  //   const filterValue = table.getColumn("amount")?.getFilterValue();
  //   if (!filterValue || filterValue[0] === undefined) {
  //     table.getColumn("amount")?.setFilterValue([minAmount, maxAmount]);
  //   }
  // }, [data, minAmount, maxAmount]);

  const handleCreatedDateRangeChange = (range) => {
    table.getColumn("date")?.setFilterValue(range);
  };

  // const formatAmount = (price) => {
  //   if (!price) return `$${Number("0").toLocaleString()}`;
  //   return price === maxAmount
  //     ? `$${price.toLocaleString()}+`
  //     : `$${price.toLocaleString()}`;
  // };

  return (
    <div className="space-y-4 mb-96">
      {/* Filters */}
      <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-3 flex-col-reverse lg:flex-row">
        <div className="flex items-center gap-3 grow flex-col lg:flex-row w-full">
          {/* Filter by from or to */}
          <SearchInput
            searchValue={table.getColumn("from")?.getFilterValue()}
            setSearchValue={table.getColumn("from")?.setFilterValue}
          />
          <div className="grow flex justify-between w-full lg:w-auto">
            <div className="flex items-center gap-3 mr-3">
              {/* Filters */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="">
                    <Filter
                      className="-ms-1 me-2 opacity-60"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                    Filters
                    {columnFilters.length > 0 && (
                      <span className="-me-1 ms-3 inline-flex h-5 max-h-full items-center rounded border border-border bg-background px-1 font-[inherit] text-[0.625rem] font-medium text-muted-foreground/70">
                        {columnFilters.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="min-w-[440px] p-3 py-0"
                  align="start"
                >
                  <div className="flex justify-between items-center my-2">
                    <div className="text-normal font-medium text-slate-800">
                      Filters
                    </div>
                    {/* <div className="text-xs font-medium text-muted-foreground"></div> */}
                    <Button
                      variant="link"
                      onClick={() =>
                        table.setColumnFilters([
                          // { id: "amount", value: [minAmount, maxAmount] },
                        ])
                      }
                    >
                      Clear all
                    </Button>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value={"status"} className="py-2 border-t">
                      <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                        Status
                      </AccordionTrigger>
                      <AccordionContent className="py-2 text-muted-foreground">
                        <div className="space-y-3">
                          {uniqueStatusValues.map((value, i) => (
                            <div
                              key={value}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`${id}-${i}`}
                                checked={selectedStatuses.includes(value)}
                                onCheckedChange={(checked) =>
                                  handleStatusChange(checked, value)
                                }
                              />
                              <Label
                                htmlFor={`${id}-${i}`}
                                className="flex grow justify-between gap-2 font-normal"
                              >
                                {value}{" "}
                                <span className="ms-2 text-xs text-muted-foreground">
                                  {statusCounts.get(value)}
                                </span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value={"dateCreated"} className="py-2">
                      <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                        Date
                      </AccordionTrigger>
                      <AccordionContent className="py-2 text-muted-foreground">
                        <CreatedDateRangeFilter
                          date={table.getColumn("date")?.getFilterValue()}
                          setDate={handleCreatedDateRangeChange}
                        />
                      </AccordionContent>
                    </AccordionItem>
                    {/* <AccordionItem value={"amount"} className="py-2">
                      <AccordionTrigger className="py-2 hover:no-underline text-sm font-medium text-muted-foreground">
                        Amount
                      </AccordionTrigger>
                      <AccordionContent className="py-2 text-muted-foreground">
                        <div className="space-y-3">
                          <Label className="tabular-nums">
                            From{" "}
                            {formatAmount(
                              table.getColumn("amount")?.getFilterValue()?.[0]
                            )}{" "}
                            to{" "}
                            {formatAmount(
                              table.getColumn("amount")?.getFilterValue()?.[1]
                            )}
                          </Label>
                          <div className="flex items-center gap-4">
                            {minAmount !== undefined &&
                              maxAmount !== undefined && (
                                <Slider
                                  value={table
                                    .getColumn("amount")
                                    ?.getFilterValue()}
                                  onValueChange={(value) =>
                                    table
                                      .getColumn("amount")
                                      ?.setFilterValue(value)
                                  }
                                  min={minAmount}
                                  max={maxAmount}
                                  minStepsBetweenThumbs={20}
                                  aria-label="Amount range slider"
                                />
                              )}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem> */}
                  </Accordion>
                </PopoverContent>
              </Popover>
              {/* Toggle columns visibility */}
              <TableColumnsManager table={table} />
            </div>
            <TableActions table={table} />
          </div>
        </div>
      </div>
      <MainTable
        ref={tableRef}
        table={table}
        isLoading={isLoading}
        onRowClick={(transaction) =>
          openTransactionDrawer(transaction?.transaction_id)
        }
      />
      <TablePagination table={table} />
    </div>
  );
}

function TableActions({ table }) {
  const { user } = useAuth();

  const transactions = table
    .getPrePaginationRowModel()
    .flatRows.map((row) => row.original);

  const mapTransactionForExport = (transaction) => {
    let res = {};
    if (!transaction) return res;
    const flatten = (source, prefix = "", exclude = []) => {
      if (!source) return;
      for (const [key, value] of Object.entries(source)) {
        if (!key.includes("id") && !exclude.includes(key)) {
          res[prefix ? `${prefix}_${key}` : key] = value;
        }
      }
    };

    // flatten(invoice.invoice_info?.invoice_from, "invoice_from");
    // flatten(invoice.invoice_info?.invoice_to, "invoice_to");
    // flatten(invoice.invoice_info, "", [
    //   "invoice_from",
    //   "invoice_to",
    //   "invoice_line_items",
    //   "invoice_full_location",
    // ]);
    // flatten(invoice, "", ["invoice_info"]);

    return res;
  };

  const exportTable = (data = []) => {
    const exclude = [
      "originating_account_id",
      "destination_account_id",
      "transaction_to_user_id",
      "transaction_from_user_id",
      "pb_transaction_fee_ind",
      "counter_transaction_id",
      "baas_info",
      "user_id",
    ];
    exportToCsv(data, exclude, "Transactions");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          Actions
          <ChevronDown
            className="-me-1 ms-2 opacity-60"
            size={16}
            strokeWidth={2}
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => exportTable(transactions)}>
          <div className="flex gap-x-2 ">
            <Download className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Export</span>
              <span className="text-xs text-muted-foreground">
                Export transactions to a csv file. The filters and sorting will
                be applied.
              </span>
            </div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CreatedDateRangeFilter({ date, setDate }) {
  const today = new Date();
  const yesterday = {
    from: subDays(today, 1),
    to: subDays(today, 1),
  };
  const last7Days = {
    from: subDays(today, 6),
    to: today,
  };
  const last30Days = {
    from: subDays(today, 29),
    to: today,
  };
  const monthToDate = {
    from: startOfMonth(today),
    to: today,
  };
  const lastMonth = {
    from: startOfMonth(subMonths(today, 1)),
    to: endOfMonth(subMonths(today, 1)),
  };
  const yearToDate = {
    from: startOfYear(today),
    to: today,
  };
  const lastYear = {
    from: startOfYear(subYears(today, 1)),
    to: endOfYear(subYears(today, 1)),
  };

  const [month, setMonth] = useState(date?.to);

  return (
    <div>
      <div className="rounded-lg border border-border">
        <div className="flex max-sm:flex-col">
          <div className="relative border-border py-4 max-sm:order-1 max-sm:border-t sm:w-36">
            <div className="h-full border-border sm:border-e">
              <div className="flex flex-col px-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate({
                      from: today,
                      to: today,
                    });
                    setMonth(today);
                  }}
                >
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(yesterday);
                    setMonth(yesterday.to);
                  }}
                >
                  Yesterday
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(last7Days);
                    setMonth(last7Days.to);
                  }}
                >
                  Last 7 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(last30Days);
                    setMonth(last30Days.to);
                  }}
                >
                  Last 30 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(monthToDate);
                    setMonth(monthToDate.to);
                  }}
                >
                  Month to date
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(lastMonth);
                    setMonth(lastMonth.to);
                  }}
                >
                  Last month
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(yearToDate);
                    setMonth(yearToDate.to);
                  }}
                >
                  Year to date
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setDate(lastYear);
                    setMonth(lastYear.to);
                  }}
                >
                  Last year
                </Button>
              </div>
            </div>
          </div>
          <Calendar
            mode="range"
            selected={date}
            onSelect={(newDate) => {
              if (newDate) {
                setDate(newDate);
              }
            }}
            month={month}
            onMonthChange={setMonth}
            className="p-2"
            disabled={[
              { after: today }, // Dates before today
            ]}
          />
        </div>
      </div>
    </div>
  );
}
